'use client'

import type { CSSProperties } from 'react'
import { AlertTriangle } from 'lucide-react'
import { reloadPage } from '@/lib/reload-page'
import { useRouteErrorReport } from '@/components/error-boundary'

/**
 * Last-resort boundary, for a crash the root layout itself could not survive.
 * It replaces the whole document, so app/globals.css is out of scope and the
 * styles below are inline on purpose: a Tailwind class here renders unstyled,
 * which is how this ends up as bare black text on a white page in the one
 * moment the visitor is already having a bad time. Colours mirror the tokens
 * in app/globals.css.
 */
const page: CSSProperties = {
  margin: 0,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  background: '#0A0A0C',
  color: '#F4F3F0',
  colorScheme: 'dark',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
}

const card: CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: '#121215',
  border: '1px solid #26262b',
  borderRadius: 12,
  padding: 24,
}

const title: CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 600,
  letterSpacing: '-0.01em',
}

const body: CSSProperties = {
  margin: '12px 0 0',
  fontSize: 14,
  lineHeight: 1.5,
  color: '#A3A3AB',
}

const buttonBase: CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useRouteErrorReport(error, 'global-error')

  return (
    <html lang="en">
      <body style={page}>
        <main style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={20} color="#DC2626" aria-hidden="true" />
            <h1 style={title}>Something went wrong</h1>
          </div>
          <p style={body}>
            The page hit an error we could not recover from. Reloading usually fixes it, and we have
            been notified.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button
              type="button"
              onClick={reset}
              style={{ ...buttonBase, background: 'transparent', border: '1px solid #3f3f46', color: '#F4F3F0' }}
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => reloadPage()}
              style={{ ...buttonBase, background: '#F4F3F0', border: '1px solid #F4F3F0', color: '#0A0A0C' }}
            >
              Reload Page
            </button>
          </div>
          <p style={{ margin: '16px 0 0', textAlign: 'center', fontSize: 13 }}>
            <button
              type="button"
              onClick={() => { window.location.href = '/' }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#A3A3AB',
                fontSize: 13,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Go home
            </button>
          </p>
        </main>
      </body>
    </html>
  )
}
